import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, InfoIcon, LoaderIcon } from 'lucide-react';

/**
 * Shared visual language for the multi-step match funnel, so the program
 * selection start page and the questionnaire that follows it read as one flow.
 */

// Brand accent, used for progress, required-field markers, and focus rings.
export const ACCENT = '#1668E0';
// Primary submit action, matching the CTA color used across the site.
export const CTA = '#FF9F04';
export const CTA_DARK = '#E08C00';
export interface StageInfo {
  label: string;
  steps: number[];
}

/**
 * The funnel's stages. Step 1 is program selection on the start page; steps
 * 2-7 are the questionnaire. Keeping one list here means the progress bar
 * never disagrees with itself between the two pages.
 */
export const MATCH_STAGES: StageInfo[] = [{
  label: 'YOUR PROGRAM',
  steps: [1]
}, {
  label: 'ABOUT YOU',
  steps: [2, 3, 4, 5]
}, {
  label: 'CONTACT INFO',
  steps: [6]
}, {
  label: 'YOUR SCHOOL MATCHES',
  steps: []
}];
export const MATCH_TOTAL_STEPS = 6;
export function StageProgress({
  currentStep
}: {
  currentStep: number;
}) {
  let activeStageIdx = 0;
  MATCH_STAGES.forEach((stage, idx) => {
    if (stage.steps.includes(currentStep)) activeStageIdx = idx;
  });

  // Fill tracks overall step progress so the bar always moves forward.
  const fillPercent = Math.min(100, Math.max(0, (currentStep - 1) / (MATCH_TOTAL_STEPS - 1) * 100));
  return <div className="w-full max-w-2xl mx-auto pt-8 pb-2" data-id="element-288" data-mp="4tbxfy13rbhf">
      {/* Stage labels */}
      <div className="grid grid-cols-4 gap-1 mb-3" data-id="element-289" data-mp="3vzdecn7r0zaz">
        {MATCH_STAGES.map((s, idx) => <div key={s.label} className={`text-center text-[10px] sm:text-[11px] font-bold tracking-widest uppercase leading-tight ${idx === activeStageIdx ? '' : 'text-gray-400'}`} style={idx === activeStageIdx ? {
        color: ACCENT
      } : undefined} data-id="element-290" data-mp="3r12265tzecpl" data-mpi={idx}>
            {s.label}
          </div>)}
      </div>

      {/* Progress bar with dots */}
      <div className="relative" data-id="element-291" data-mp="3ce3d2hiljue1">
        <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 bg-gray-200 rounded-full" data-id="element-292" data-mp="3cf64qrnqu0v0" />
        <motion.div className="absolute top-1/2 left-0 h-1 -translate-y-1/2 rounded-full" style={{
        backgroundColor: ACCENT
      }} initial={false} animate={{
        width: `${fillPercent}%`
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-293" data-mp="2yfdgd2zovwq7" />
        <div className="relative flex justify-between items-center" data-id="element-294" data-mp="3hdpqos2c7cnd">
          {MATCH_STAGES.map((s, idx) => {
          const isActive = idx <= activeStageIdx;
          return <div key={s.label} className={`w-5 h-5 rounded-full border-2 transition-colors ${isActive ? '' : 'bg-white border-gray-300'}`} style={isActive ? {
            backgroundColor: ACCENT,
            borderColor: ACCENT
          } : undefined} data-id="element-295" data-mp="dwr2c5rv5233" data-mpi={idx} />;
        })}
        </div>
      </div>

      <p className="text-center text-sm text-gray-500 mt-4" data-id="element-296" data-mp="wspyrb67b10i">
        Step {currentStep} of {MATCH_TOTAL_STEPS}
      </p>
    </div>;
}
export function InfoBanner({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="mt-5 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4" data-id="element-297" data-mp="521ys2g9k1of">
      <div className="flex-shrink-0 mt-0.5 text-blue-600" data-id="element-298" data-mp="j1unjs88c7tr">
        <InfoIcon className="w-4 h-4" data-id="element-299" data-mp="iz8gdtp93uc2" />
      </div>
      <p className="text-xs sm:text-sm text-blue-700 leading-relaxed" data-id="element-300" data-mp="e1yqye1v3io8">
        {children}
      </p>
    </div>;
}
interface StepCardProps {
  title: string;
  subtitle?: string;
  onContinue: () => void;
  ctaLabel: string;
  ctaIcon?: React.ReactNode;
  info?: React.ReactNode;
  error?: string;
  isSubmitting?: boolean;
  children: React.ReactNode;
}
export function StepCard({
  title,
  subtitle,
  onContinue,
  ctaLabel,
  ctaIcon,
  info,
  error,
  isSubmitting,
  children
}: StepCardProps) {
  return (
    // A real <form> with a real submit button. Jornaya and TrustedForm both
    // certify a form element and the interactions inside it; a div full of
    // inputs with a type="button" reads as non-authentic.
    <form onSubmit={e => {
      e.preventDefault();
      onContinue();
    }} noValidate className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-10" data-id="element-301" data-mp="hycjtcc23kpy">
      {/* Certification fields. The scripts write into these by id/name, so they
          are left uncontrolled and must live inside the form element. */}
      <input type="hidden" id="leadid_token" name="leadid_token" data-id="element-302" data-mp="10px3tpy4ikrr" />
      <input type="hidden" id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" data-id="element-303" data-mp="10nkcwnae102k" />

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center leading-tight mb-2" data-id="element-304" data-mp="15mdtwpovv97h">
        {title}
      </h1>
      {subtitle && <p className="text-center text-sm text-gray-500 mb-6" data-id="element-305" data-mp="2l6mtzxcyczxp">{subtitle}</p>}
      <div className={subtitle ? 'mt-2' : 'mt-6'} data-id="element-306" data-mp="2h5ayjb7edkhl">{children}</div>

      {error && <p className="mt-3 text-sm text-red-600 text-center" data-id="element-307" data-mp="2c7j70iao00dx">{error}</p>}

      <button type="submit" disabled={isSubmitting} className="mt-6 w-full text-white font-bold py-4 px-6 rounded-lg text-base transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed" style={{
        backgroundColor: CTA
      }} onMouseEnter={e => {
        if (!isSubmitting) (e.currentTarget as HTMLButtonElement).style.backgroundColor = CTA_DARK;
      }} onMouseLeave={e => {
        ;
        (e.currentTarget as HTMLButtonElement).style.backgroundColor = CTA;
      }} data-id="element-308" data-mp="38x8y5dju602f">
        {isSubmitting ? <span className="inline-flex items-center gap-2" data-id="element-309" data-mp="amf44cn6p7nw">
            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-310" data-mp="fkguzjquqb9x" />
            {ctaLabel}
          </span> : <>
            {ctaLabel}
            {ctaIcon ? ctaIcon : <ArrowRightIcon className="w-5 h-5 ml-2 inline-block" data-id="element-311" data-mp="5u5fczxt2dn7" />}
          </>}
      </button>

      {info && <InfoBanner data-id="element-312" data-mp="3nzijexqso75g">{info}</InfoBanner>}
    </form>
  );
}
interface SelectFieldProps {
  value: string;
  onChange: (v: string) => void;
  options: {
    label: string;
    value: string;
  }[];
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}
export function SelectField({
  value,
  onChange,
  options,
  id,
  label,
  placeholder = '-- Please Select --',
  disabled
}: SelectFieldProps) {
  return <div data-id="element-313" data-mp="1d64iff5bxj9b">
      {label && <label htmlFor={id} className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-314" data-mp="1h9kcc3q0l5ib">
          {label}{' '}
          <span style={{
        color: ACCENT
      }} data-id="element-315" data-mp="31so8hf12ahal">
            *
          </span>
        </label>}
      <select id={id} value={value} onChange={e => onChange(e.target.value)} disabled={disabled} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed" style={{
      '--tw-ring-color': ACCENT,
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 0.75rem center',
      backgroundSize: '1.25rem',
      paddingRight: '2.5rem'
    } as React.CSSProperties} data-id="element-316" data-mp="3bho6av3tv6n7">
        <option value="" data-id="element-317" data-mp="1e8k5twronsd1">{placeholder}</option>
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-318" data-mp="1j0byqdybaa6c">
            {opt.label}
          </option>)}
      </select>
    </div>;
}